import { BUILTIN_PREDICATES } from './builtins.js';
import { loadPacks, OFFICIAL_CORE_PACK_ID } from './pack-loader.js';
import { type PredicateDeclaration } from './predicate.js';

/**
 * §7.2 — the predicates a controlling player may write DIRECTLY on their PC,
 * landing as canon without GM ratification. A predicate opts in via
 * `player_authority: true` on its declaration (II.B.5 / #52).
 *
 * The set here is the always-present baseline: the built-in `name` plus the
 * official-core identity predicates (`appearance`, `portrait`, `visited`).
 * Homebrew / genre-pack predicates that set the flag are NOT included — the
 * op-accept gate may union those from the campaign's folded `__pack` defs
 * (passed via `extra`) when that lands; today the baseline is the gate.
 *
 * The flag is necessary-but-not-sufficient: the op-accept gate ALSO requires
 * the writer to control the op's subject (a `controlled_by` fact), so flagging
 * `name` here never lets a player rename an entity they don't control.
 */
let cache: ReadonlySet<string> | null = null;

function baselineIds(): ReadonlySet<string> {
  if (cache) return cache;
  const ids = new Set<string>();
  for (const decl of BUILTIN_PREDICATES) {
    if (decl.player_authority) ids.add(decl.id);
  }
  for (const pack of loadPacks([OFFICIAL_CORE_PACK_ID]).packs) {
    for (const decl of pack.predicates) {
      if (decl.player_authority) ids.add(decl.id);
    }
  }
  cache = ids;
  return ids;
}

/** The baseline player-authority predicate id set (memoized). */
export function playerAuthorityPredicateIds(): ReadonlySet<string> {
  return baselineIds();
}

/**
 * Is `predicateId` writable directly by a controlling player? Checks the
 * always-present baseline plus any extra declarations (e.g. homebrew predicates
 * folded from the op log's `__pack` ops) the caller supplies.
 */
export function isPlayerAuthorityPredicate(
  predicateId: string,
  extra: readonly PredicateDeclaration[] = [],
): boolean {
  if (baselineIds().has(predicateId)) return true;
  for (const decl of extra) {
    if (decl.id === predicateId && decl.player_authority) return true;
  }
  return false;
}
